import React, { useEffect, useState, useMemo } from "react";
import {
  Loader2, CheckCircle2, XCircle, AlertTriangle, Sparkles, ChevronRight,
  ArrowLeft, Plus, Wand2, ShieldAlert, CircleDot, Target, TrendingUp, ShieldCheck, Info,
} from "lucide-react";
import { WaitingUX } from "../components/WaitingUX";
import { useJobProgress } from "../lib/useJobProgress";

const PHASES = [
  { key: "parse", label: "Parsing resume" },
  { key: "extract", label: "Extracting JD keywords" },
  { key: "score", label: "Scoring matches" },
  { key: "plan", label: "Building action plan" },
];

const PRIORITY_CHIP = {
  high: "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400",
  medium: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  low: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
};

const STATE_STYLE = {
  matched: "text-emerald-700 bg-emerald-100 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-950/30 dark:border-emerald-900",
  partial: "text-amber-700 bg-amber-100 border-amber-200 dark:text-amber-400 dark:bg-amber-950/30 dark:border-amber-900",
  missing: "text-rose-700 bg-rose-100 border-rose-200 dark:text-rose-400 dark:bg-rose-950/30 dark:border-rose-900",
};

const STATE_ICON = { matched: CheckCircle2, partial: CircleDot, missing: XCircle };

const SUBFACTOR_LABELS = {
  seniority_fit: "Seniority Fit",
  depth_in_top_skills: "Top Skills Depth",
  domain_match: "Domain Match",
  standout_factors: "Standout Factors",
};

const ACTION_GROUPS = [
  { key: "strengthen", title: "Strengthen what you already have", icon: TrendingUp,
    accent: "text-sky-700 dark:text-sky-300",
    container: "bg-sky-50/70 dark:bg-sky-950/20 border-sky-200/80 dark:border-sky-900/60",
    hint: "Already on your resume — we'll surface these more prominently where credible." },
  { key: "green", title: "Safe to add — fits your background", icon: ShieldCheck,
    accent: "text-emerald-700 dark:text-emerald-300",
    container: "bg-emerald-50/70 dark:bg-emerald-950/20 border-emerald-200/80 dark:border-emerald-900/60",
    hint: "Woven naturally into your experience and skills section, anchored to your real work. Guaranteed to land." },
  { key: "yellow", title: "Recommended — related to your field", icon: AlertTriangle,
    accent: "text-amber-700 dark:text-amber-300",
    container: "bg-amber-50/70 dark:bg-amber-950/20 border-amber-200/80 dark:border-amber-900/60",
    hint: "Related to your background but with thinner evidence. With your OK we weave each one credibly, building on your real experience." },
  { key: "red", title: "Different field — we suggest leaving these off", icon: ShieldAlert,
    accent: "text-rose-700 dark:text-rose-300",
    container: "bg-rose-50/70 dark:bg-rose-950/20 border-rose-200/80 dark:border-rose-900/60",
    hint: "These belong to a genuinely different profession, so weaving them into your experience could read as fabricated. With your OK they're added to your skills section only." },
  { key: "enhance", title: "Strengthen your content", icon: Wand2,
    accent: "text-indigo-700 dark:text-indigo-300",
    container: "bg-indigo-50/70 dark:bg-indigo-950/20 border-indigo-200/80 dark:border-indigo-900/60",
    hint: "Sharper summary and bullets, aligned to this role." },
  { key: "guard", title: "Credibility guards", icon: ShieldAlert,
    accent: "text-amber-700 dark:text-amber-300",
    container: "bg-amber-50/70 dark:bg-amber-950/20 border-amber-200/80 dark:border-amber-900/60",
    hint: "Optional honesty fixes — tick the ones you'd like us to handle." },
];

function ScoreRing({ value, size = 144 }) {
  const v = Math.max(0, Math.min(100, Math.round(value || 0)));
  const r = 52, c = 2 * Math.PI * r;
  const color = v >= 75 ? "#059669" : v >= 50 ? "#d97706" : "#e11d48";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="match-score-ring">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="currentColor" strokeWidth="9" className="text-border/60" />
        <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (v / 100) * c} style={{ transition: "stroke-dashoffset 1s ease-out" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-black tracking-tighter text-foreground">{v}<span className="text-xl">%</span></span>
        <span className="text-[0.62rem] font-mono uppercase tracking-wider text-muted-foreground">Match</span>
      </div>
    </div>
  );
}

function LivePhaseStepper({ activeIndex, done }) {
  return (
    <div className="hidden md:flex items-center gap-1" data-testid="live-phase-stepper">
      {PHASES.map((p, i) => {
        const complete = done || i < activeIndex;
        const active = !done && i === activeIndex;
        return (
          <React.Fragment key={p.key}>
            <span className={`flex items-center gap-1.5 text-xs ${active ? "text-foreground font-semibold" : complete ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground/50"}`}>
              {complete ? <CheckCircle2 size={13} /> : active ? <Loader2 size={13} className="animate-spin" /> : <CircleDot size={13} />}
              {p.label}
            </span>
            {i < PHASES.length - 1 && <ChevronRight size={13} className="text-muted-foreground/30" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ScanningView({ activeIndex, modelName }) {
  const scanProgress = useJobProgress({ active: true, phase: "scan" });
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-20" data-testid="scanning-view">
      <div className="relative mb-8">
        <div className="w-20 h-20 rounded-2xl bg-primary/5 border border-border flex items-center justify-center">
          <Target size={32} className="text-foreground" />
        </div>
        <span className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-background border border-border flex items-center justify-center">
          <Loader2 size={16} className="animate-spin text-blue-600 dark:text-blue-400" />
        </span>
      </div>
      <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-foreground mb-2 text-center">Analyzing your resume</h2>
      <p className="text-sm text-muted-foreground mb-10 text-center">
        {`${modelName} is working — this can take up to ~2 minutes.`}
      </p>
      <div className="w-full max-w-md flex flex-col gap-3">
        {PHASES.map((p, i) => {
          const complete = i < activeIndex;
          const active = i === activeIndex;
          return (
            <div key={p.key} className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all duration-300 ${active ? "border-primary bg-primary/5" : complete ? "border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/20" : "border-border/50 bg-background"}`}>
              {complete ? <CheckCircle2 size={18} className="text-emerald-600 dark:text-emerald-400" />
                : active ? <Loader2 size={18} className="animate-spin text-foreground" />
                : <CircleDot size={18} className="text-muted-foreground/40" />}
              <span className={`text-sm ${active ? "font-semibold text-foreground" : complete ? "text-foreground" : "text-muted-foreground"}`}>{p.label}</span>
            </div>
          );
        })}
      </div>
      <WaitingUX className="w-full max-w-md mt-8" progress={scanProgress} />
    </div>
  );
}

function KeywordRow({ it, group, idx }) {
  const Icon = STATE_ICON[it.state] || CircleDot;
  return (
    <div className="flex items-center justify-between gap-3 py-3 px-5 border-b border-border/30 last:border-0 hover:bg-muted/20 transition-colors" data-testid={`keyword-row-${group}-${idx}`}>
      <div className="flex items-center gap-2.5 min-w-0">
        <Icon size={16} className={it.state === "matched" ? "text-emerald-600 dark:text-emerald-400" : it.state === "partial" ? "text-amber-600 dark:text-amber-400" : "text-rose-600 dark:text-rose-400"} />
        <span className="font-medium text-foreground truncate">{it.skill}</span>
        <span className={`text-[0.6rem] font-mono uppercase px-1.5 py-0.5 rounded ${it.importance === "required" ? "bg-foreground/10 text-foreground" : "bg-muted text-muted-foreground"}`}>{it.importance === "required" ? "Req" : "Pref"}</span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="hidden xl:inline text-[0.7rem] font-mono text-muted-foreground">{it.jd_count}× JD · {it.resume_count}× résumé</span>
        <span className={`rounded-full px-2.5 py-0.5 text-sm font-semibold border ${STATE_STYLE[it.state]}`}>{it.earned}/{it.max}</span>
      </div>
    </div>
  );
}

export default function AnalysisStep({
  scanning, scanData, modelName,
  selectedActions, onToggleAction, onSelectAll,
  skillPlacements = {}, onSetPlacement,
  resumeRename, setResumeRename,
  onBack, onOptimize, processing,
}) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!scanning) { setActiveIndex(PHASES.length); return; }
    setActiveIndex(0);
    const id = setInterval(() => setActiveIndex((i) => (i < PHASES.length - 1 ? i + 1 : i)), 2200);
    return () => clearInterval(id);
  }, [scanning]);

  const kb = scanData?.keyword_breakdown;
  const actions = scanData?.action_plan || [];
  const sub = scanData?.competitiveness?.sub_factors || {};
  const grouped = useMemo(() => {
    const g = { strengthen: [], green: [], yellow: [], red: [], enhance: [], guard: [] };
    actions.forEach((a) => {
      if (a.type === "add_skill") {
        const bucket = a.bucket && g[a.bucket] ? a.bucket : (a.consent_required ? "yellow" : "green");
        g[bucket].push(a);
      } else if (a.type === "resolve_contradiction" || a.type === "resolve_anachronism") g.guard.push(a);
      else g.enhance.push(a);
    });
    return g;
  }, [actions]);

  const selectedCount = selectedActions.length;
  const allSelected = actions.length > 0 && selectedCount === actions.length;

  const renderActionCard = (a) => {
    const checked = selectedActions.includes(a.id);
    return (
      <div key={a.id} role="button" tabIndex={0} onClick={() => onToggleAction(a.id)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onToggleAction(a.id); }}
        data-testid={`action-card-${a.id}`}
        className={`text-left cursor-pointer rounded-xl p-4 border transition-all duration-200 active:scale-[0.99] ${checked ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border/80 hover:border-primary/50 bg-card"}`}>
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 w-5 h-5 rounded-md shrink-0 flex items-center justify-center border ${checked ? "bg-primary border-primary" : "border-muted-foreground/40 bg-background"}`}>
            {checked && <CheckCircle2 size={14} className="text-primary-foreground" />}
          </span>
          <div className="min-w-0 flex-1">
            <span className="text-sm font-medium text-foreground">{a.label}</span>
            <p className="text-xs text-muted-foreground mt-1">{a.detail}</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`text-[0.6rem] font-mono uppercase px-1.5 py-0.5 rounded ${PRIORITY_CHIP[a.priority] || PRIORITY_CHIP.medium}`}>{a.priority}</span>
              {a.type === "add_skill" && a.specificity === "specific" && (
                <span className="text-[0.6rem] font-mono uppercase px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 flex items-center gap-1" data-testid={`specific-chip-${a.skill}`}>
                  <Target size={10} /> Specific requirement
                </span>
              )}
              {a.version_gap && (
                <span className="text-[0.6rem] font-mono uppercase px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300 flex items-center gap-1" data-testid={`version-chip-${a.skill}`}>
                  <Info size={10} /> Version check
                </span>
              )}
              {a.section_only && <span className="text-[0.6rem] font-mono uppercase px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400" data-testid={`section-only-chip-${a.skill}`}>Skills section only</span>}
              {a.consent_required && !a.section_only && <span className="text-[0.6rem] font-mono uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">Optional · your choice</span>}
            </div>
            {a.version_gap && a.version_note && (
              <p className="text-[0.72rem] text-violet-700 dark:text-violet-300 mt-2 flex items-start gap-1.5" data-testid={`version-note-${a.skill}`}>
                <Info size={12} className="mt-0.5 shrink-0" /> {a.version_note}
              </p>
            )}
            {a.bucket === "red" && checked && onSetPlacement && (
              <div className="mt-2.5 flex flex-col gap-1.5 border-t border-border/50 pt-2" onClick={(e) => e.stopPropagation()}>
                <span className="text-[0.65rem] font-semibold text-muted-foreground uppercase tracking-wide">Where should it go?</span>
                {[["section_only", "Skills section only (recommended)"], ["weave", "Also weave into my experience — I understand it may look less credible"]].map(([val, lab]) => (
                  <label key={val} className="flex items-start gap-2 text-[0.72rem] cursor-pointer" data-testid={`placement-${val}-${a.skill}`}>
                    <input type="radio" name={`placement-${a.id}`} className="mt-0.5 accent-rose-500"
                      checked={(skillPlacements[a.skill] || "section_only") === val}
                      onChange={() => onSetPlacement(a.skill, val)} />
                    <span className={val === "weave" ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground"}>{lab}</span>
                  </label>
                ))}
                {(skillPlacements[a.skill] === "weave") && (
                  <p className="text-[0.68rem] text-rose-600/90 dark:text-rose-400/90 flex items-start gap-1"><AlertTriangle size={11} className="mt-0.5 shrink-0" /> We'll integrate it as naturally as possible and flag it in your results so you can prepare for interview questions.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderGroup = (grp, items) => {
    const GIcon = grp.icon;
    return (
      <div key={grp.key} className={`rounded-2xl border p-4 flex flex-col gap-2.5 ${grp.container}`} data-testid={`action-group-${grp.key}`}>
        <div className="flex items-center gap-2">
          <GIcon size={16} className={grp.accent} />
          <span className="text-sm font-semibold text-foreground">{grp.title}</span>
          <span className="text-xs text-muted-foreground">· {items.length}</span>
        </div>
        <p className="text-xs text-muted-foreground -mt-1">{grp.hint}</p>
        {items.map(renderActionCard)}
      </div>
    );
  };

  const actionGroupEls = (() => {
    const els = [];
    const hasAbove = ["strengthen", "green", "yellow"].some((k) => (grouped[k] || []).length > 0);
    ACTION_GROUPS.forEach((grp) => {
      const items = grouped[grp.key];
      if (!items || items.length === 0) return;
      if (grp.key === "red" && hasAbove) {
        els.push(
          <div key="divider-irrelevant" className="relative flex items-center gap-3 py-1" data-testid="divider-line-irrelevant">
            <div className="flex-1 border-t-2 border-dashed border-rose-300 dark:border-rose-800" />
            <span className="inline-flex items-center gap-1.5 text-[0.68rem] font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300 bg-rose-100 dark:bg-rose-950/40 px-3 py-1 rounded-full whitespace-nowrap">
              <ShieldAlert size={12} /> We suggest leaving these off
            </span>
            <div className="flex-1 border-t-2 border-dashed border-rose-300 dark:border-rose-800" />
          </div>
        );
      }
      els.push(renderGroup(grp, items));
    });
    return els;
  })();

  const KW_GROUPS = [
    { key: "technical", title: "Hard / Technical Skills" },
    { key: "soft", title: "Soft Skills" },
    { key: "domain", title: "Domain Terms" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-background overflow-y-auto flex flex-col" data-testid="analysis-step">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/40 h-16 px-4 lg:px-6 flex items-center justify-between shrink-0">
        <button onClick={onBack} disabled={processing} className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="analysis-back-btn">
          <ArrowLeft size={16} /> <span className="hidden sm:inline">Back</span>
        </button>
        <LivePhaseStepper activeIndex={activeIndex} done={!scanning} />
        <span className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-blue-600 dark:text-blue-400" data-testid="analysis-engine-badge">
          <Sparkles size={13} /> {modelName}
        </span>
      </header>

      {scanning || !scanData ? (
        <ScanningView activeIndex={activeIndex} modelName={modelName} />
      ) : (
        <>
          <div className="max-w-7xl mx-auto w-full px-4 lg:px-6 py-8 pb-36 flex flex-col gap-8">
            {/* Full-width score hero band */}
            <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-12 items-center p-6 lg:p-8 bg-muted/20 rounded-2xl border border-border/50" data-testid="score-hero">
              <div className="flex items-center gap-6 justify-center lg:justify-start">
                <ScoreRing value={kb?.match_score} />
                <div className="flex flex-col gap-2">
                  <div className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-muted-foreground">Keyword Match</div>
                  <div className="flex flex-col gap-1 text-sm">
                    <span className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400"><CheckCircle2 size={14} /> {kb?.matched_count || 0} matched</span>
                    <span className="flex items-center gap-2 text-amber-600 dark:text-amber-400"><CircleDot size={14} /> {kb?.partial_count || 0} partial</span>
                    <span className="flex items-center gap-2 text-rose-600 dark:text-rose-400"><XCircle size={14} /> {kb?.missing_count || 0} missing</span>
                  </div>
                  <div className="text-xs font-mono text-muted-foreground mt-1">{kb?.total_earned || 0}/{kb?.total_max || 0} pts</div>
                </div>
              </div>
              <div className="flex flex-col justify-center gap-2.5 lg:border-l lg:border-border/60 lg:pl-12" data-testid="competitiveness-score-bars">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-muted-foreground">Competitiveness</span>
                  <span className="text-sm font-bold text-foreground">{Math.round(scanData?.competitiveness?.competitiveness_score || 0)}/100</span>
                </div>
                {Object.entries(SUBFACTOR_LABELS).map(([key, label]) => {
                  const val = Math.max(0, Math.min(100, Math.round(sub[key] || 0)));
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">{label}</span>
                        <span className="font-medium text-foreground">{val}%</span>
                      </div>
                      <div className="h-1.5 w-full bg-border rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${val}%`, transition: "width 0.8s ease-out" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Action plan (grouped, tickable) + keyword breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
              {/* LEFT: grouped, tickable action plan */}
              <div className="flex flex-col gap-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold tracking-tight text-foreground flex items-center gap-2"><Wand2 size={18} /> Your action plan</h3>
                  <button onClick={() => onSelectAll(!allSelected)} className="text-xs font-semibold text-primary hover:underline" data-testid="select-all-actions">
                    {allSelected ? "Clear all" : "Select all"}
                  </button>
                </div>
                <p className="text-sm text-muted-foreground -mt-3">Tick what you want — we apply it automatically on Optimize. Every change is grounded in your real experience; nothing is fabricated.</p>

                <div className="flex flex-col gap-6" data-testid="action-panel-cards">
                  {actions.length > 0 && (
                    <div className="rounded-xl border border-indigo-200/80 dark:border-indigo-900/60 bg-indigo-50/70 dark:bg-indigo-950/20 p-4 flex items-start gap-3" data-testid="explainer-banner">
                      <Info size={16} className="text-indigo-600 dark:text-indigo-400 mt-0.5 shrink-0" />
                      <p className="text-xs leading-relaxed text-indigo-900/90 dark:text-indigo-200/90">
                        <span className="font-semibold">We keep your resume believable.</span> Skills that fit you are ready to add; related ones are recommended and woven in naturally — no exaggeration, no keyword-stuffing. Skills from a different field can look fake to recruiters, so we suggest leaving the ones <span className="font-semibold">below the line</span> off.
                      </p>
                    </div>
                  )}
                  {(scanData?.structural_warnings || []).length > 0 && (
                    <div className="rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/20 p-4 flex flex-col gap-1.5" data-testid="structural-warnings-box">
                      <span className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                        <AlertTriangle size={13} /> Things a recruiter may notice (we report these honestly — we never invent filler)
                      </span>
                      {scanData.structural_warnings.map((w, i) => (
                        <p key={i} className="text-xs text-amber-800/90 dark:text-amber-300/90">• {w}</p>
                      ))}
                    </div>
                  )}
                  {actions.length === 0 && (
                    <div className="bg-card border border-border/50 rounded-xl shadow-sm text-sm text-muted-foreground p-5">Your resume already covers this job well — no additions needed.</div>
                  )}
                  {actionGroupEls}
                </div>

                <div className="mt-1">
                  <label className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">Output file name (optional)</label>
                  <input value={resumeRename} onChange={(e) => setResumeRename(e.target.value)} data-testid="resume-rename-input"
                    placeholder="e.g. Resume_Company_Role"
                    className="mt-1.5 w-full px-3 py-2 text-sm rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
              </div>

              {/* RIGHT: keyword breakdown reference */}
              <div className="flex flex-col gap-5 lg:sticky lg:top-20">
                <div className="bg-card border border-border/50 rounded-xl overflow-hidden shadow-sm" data-testid="keyword-breakdown-list">
                  <div className="px-5 py-4 border-b border-border/50">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold tracking-tight text-foreground">JD Keyword Breakdown</h3>
                      <span className="text-xs font-mono text-muted-foreground">{kb?.keyword_count || 0} keywords</span>
                    </div>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400"><CheckCircle2 size={12} /> {kb?.matched_count || 0} matched</span>
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400"><CircleDot size={12} /> {kb?.partial_count || 0} partial</span>
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-950/30 dark:text-rose-400"><XCircle size={12} /> {kb?.missing_count || 0} missing</span>
                    </div>
                  </div>
                  <div className="max-h-[58vh] overflow-y-auto">
                    {KW_GROUPS.map((grp) => {
                      const items = kb?.groups?.[grp.key] || [];
                      if (items.length === 0) return null;
                      return (
                        <div key={grp.key}>
                          <div className="px-5 py-2 bg-muted/30 text-[0.7rem] font-mono uppercase tracking-[0.15em] text-muted-foreground sticky top-0 z-10">{grp.title} · {items.length}</div>
                          {items.map((it, idx) => <KeywordRow key={idx} it={it} group={grp.key} idx={idx} />)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky footer */}
          <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/90 backdrop-blur-xl border-t border-border/40 p-4 lg:px-8" data-testid="optimize-submit-footer">
            <div className="flex items-center justify-between w-full max-w-7xl mx-auto gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground">{selectedCount} action{selectedCount === 1 ? "" : "s"} selected</div>
                <div className="text-xs text-muted-foreground truncate">Current match {kb?.match_score || 0}% · we'll lift it where it's credible</div>
              </div>
              <button onClick={onOptimize} disabled={processing} data-testid="analysis-optimize-btn"
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60 shrink-0">
                {processing ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                {processing ? "Optimizing…" : "Optimize My Resume"}
                <span className="hidden sm:inline text-[0.6rem] font-mono px-1.5 py-0.5 rounded bg-primary-foreground/20">{modelName}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
